import { createSelector } from 'reselect';
import { RootState } from 'store';
import adapter from './orders.adapter';
import { OrderParams, OrderBodyParams } from './orders.types';
import { REDUCER_KEY } from './orders.consts';
import { selectBasket } from 'store/baskets';

const selectors = adapter.getSelectors(
  (state: RootState) => state[REDUCER_KEY],
);
export const { selectIds, selectTotal, selectAll } = selectors;

export const selectById = (state: RootState, id: number): OrderParams | null =>
  selectors.selectById(state, id) || null;

export const selectCurrentOrderId = (state: RootState): number =>
  state[REDUCER_KEY].current;

export const selectAllOrders = createSelector(
  state => selectors.selectAll(state),
  finalizedOrders => finalizedOrders || [],
);

export const selectCurrentOrder = createSelector(
  state => selectCurrentOrderId(state),
  state => selectAll(state),
  (currentOrderId, allOrders) =>
    allOrders.find(({ id }) => id === currentOrderId) || null,
);

export function selectOrder(
  state: RootState,
  orderId: number,
): OrderParams | undefined {
  return selectors.selectById(state, orderId) || undefined;
}

export const selectCurrentOrderStatus = createSelector(
  selectCurrentOrder,
  order => order?.status || null,
);

export const selectOrderStatusById = (
  state: RootState,
  orderId: number,
): string | undefined => {
  const order = selectOrder(state, orderId);
  return order?.status;
};

export const selectAllOrderValuesOfBasketById = (
  state: RootState,
  basketId: number,
): OrderBodyParams[] => {
  const orders = selectOrdersOfBasket(state, basketId);
  return orders.map(order => ({
    value: order.currentUpdate.value,
    commentary: order.currentUpdate.commentary || '',
  }));
};

export const selectOrderFormValuesById = (
  state: RootState,
  orderId: number,
): OrderBodyParams | undefined => {
  const order = selectOrder(state, orderId);
  if (!order) return undefined;
  const currentValues = order.currentUpdate;

  return {
    value: currentValues.value,
    commentary: currentValues.commentary || '',
  };
};

export const selectOrdersOfBasket = (
  state: RootState,
  basketId: number,
): OrderParams[] => {
  const orders = selectors.selectAll(state);
  const basketOrders = selectBasket(state, basketId)?.orders || [];

  return orders.filter(order => basketOrders.includes(order.id));
};
